import { useEffect, useState } from "react";
import { Flag, Loader2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { toast } from "sonner";
import { MODES, MODE_META } from "../../lib/modes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Master admin: reset the campaign (linear warm-up / bonus progression) for one user or everyone.
// Step 1 picks which campaigns (Junior Blocks / Blocks / Python), step 2 is the "Are you sure?" confirmation.
export function CampaignResetDialog({ open, onClose, user = null, onDone }) {
  const [modes, setModes] = useState(MODES);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const who = user ? `"${user.name}"` : "ALL registered users";

  useEffect(() => { if (open) { setModes(MODES); setStep(1); setBusy(false); } }, [open]);

  const toggle = (m) => setModes((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  const labels = MODES.filter((m) => modes.includes(m)).map((m) => MODE_META[m].label);

  const run = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/campaign/reset`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ modes, user_id: user?.user_id || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Reset failed");
      toast.success(user ? `Campaign reset for ${user.name}: ${labels.join(", ")}` : `Campaign reset for ${data.reset} users: ${labels.join(", ")}`);
      onDone?.(data);
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="campaign-reset-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Flag className="w-5 h-5 text-primary" /> Reset campaign {user ? `for ${user.name}` : "for all users"}</DialogTitle>
          <DialogDescription>
            The campaign is the ordered run of warm-ups and bonus challenges. Resetting sends {user ? "this user" : "every registered user"} back to question 1. Points and answer history are kept; questions answered before the reset earn the re-answer bonus only.
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-2" data-testid="campaign-reset-step-modes">
            <p className="text-sm font-medium">Which campaigns?</p>
            {MODES.map((m) => {
              const Icon = MODE_META[m].icon;
              return (
                <label key={m} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2 cursor-pointer hover:bg-muted/40">
                  <Checkbox checked={modes.includes(m)} onCheckedChange={() => toggle(m)} data-testid={`campaign-reset-mode-${m}`} />
                  <Icon className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">{MODE_META[m].label} campaign</span>
                </label>
              );
            })}
          </div>
        ) : (
          <div className="flex items-start gap-3 rounded-xl border border-destructive/40 bg-destructive/5 p-4" data-testid="campaign-reset-step-confirm">
            <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold">Are you sure?</p>
              <p className="text-muted-foreground mt-1">This resets the <span className="font-medium text-foreground">{labels.join(", ")}</span> campaign{labels.length > 1 ? "s" : ""} for <span className="font-medium text-foreground">{who}</span>. This cannot be undone.</p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => (step === 2 ? setStep(1) : onClose())} disabled={busy} data-testid="campaign-reset-back">{step === 2 ? "Back" : "Cancel"}</Button>
          {step === 1 ? (
            <Button onClick={() => setStep(2)} disabled={!modes.length} data-testid="campaign-reset-continue">Continue</Button>
          ) : (
            <Button variant="destructive" onClick={run} disabled={busy} className="gap-2" data-testid="campaign-reset-confirm">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Yes, reset {user ? "their" : "everyone's"} campaign
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
